// QuestionRenderer — renders one question input based on its type.
// Writes into `value`; parent owns persistence. Voice input slot sits
// below the textarea for `long_text` (voice-input-dev wires this up).

import { useEffect, useRef, type ReactNode } from 'react'
import type { PackQuestion } from '@/lib/api'
import { getStoredLang } from '@/lib/api'
import { t } from '@/lib/i18n'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Markdown } from '@/components/Markdown'
import { DatePickerField } from '@/components/DatePickerField'

export interface QuestionValue {
  answer_text?: string | null
  answer_choice?: string | null
  answer_number?: number | null
}

interface Props {
  question: PackQuestion
  value: QuestionValue
  onChange: (next: QuestionValue) => void
  voiceInput?: ReactNode
}

function AutosizeTextarea({
  value,
  onChange,
  placeholder,
  id,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  id?: string
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.max(el.scrollHeight, 120)}px`
  }, [value])
  return (
    <Textarea
      id={id}
      ref={ref}
      value={value}
      placeholder={placeholder ?? t(getStoredLang(), 'type_your_answer')}
      onChange={(e) => onChange(e.target.value)}
      className="font-serif text-lg leading-relaxed min-h-[120px] resize-none overflow-hidden"
    />
  )
}

export function QuestionRenderer({ question, value, onChange, voiceInput }: Props) {
  const text = value.answer_text ?? ''
  const choice = value.answer_choice ?? ''
  const num = value.answer_number ?? ''

  // Normalise: packs may use `choices` or `options` interchangeably.
  const options = question.choices ?? question.options ?? []

  // checkbox_group: `answer_choice` stored as JSON array of strings.
  let checkedSet: Set<string> = new Set()
  if (question.type === 'checkbox_group' && choice) {
    try {
      const parsed = JSON.parse(choice)
      if (Array.isArray(parsed)) checkedSet = new Set(parsed.map(String))
    } catch {
      /* fall through */
    }
  }

  return (
    <div className="space-y-3">
      <Label
        htmlFor={`q-${question.id}`}
        className="block font-serif text-xl sm:text-2xl leading-snug text-foreground"
      >
        <Markdown inline className="markdown-body">{question.prompt}</Markdown>
      </Label>
      {question.hint && (
        <Markdown inline className="text-sm text-muted-foreground italic markdown-body block">
          {question.hint}
        </Markdown>
      )}

      {question.type === 'long_text' && (
        <div className="relative">
          <AutosizeTextarea
            id={`q-${question.id}`}
            value={text}
            onChange={(v) => onChange({ answer_text: v })}
          />
          {voiceInput && (
            <div className="absolute right-2 bottom-2 pointer-events-auto">
              {voiceInput}
            </div>
          )}
        </div>
      )}

      {question.type === 'short_text' && (
        <Input
          id={`q-${question.id}`}
          value={text}
          onChange={(e) => onChange({ answer_text: e.target.value })}
          placeholder={question.placeholder ?? t(getStoredLang(), 'type_your_answer')}
          className="font-serif text-lg"
        />
      )}

      {question.type === 'email' && (
        <Input
          id={`q-${question.id}`}
          type="email"
          autoComplete="email"
          inputMode="email"
          value={text}
          onChange={(e) => onChange({ answer_text: e.target.value })}
          placeholder={question.placeholder ?? 'you@example.com'}
          className="font-serif text-lg"
        />
      )}

      {question.type === 'url' && (
        <Input
          id={`q-${question.id}`}
          type="url"
          inputMode="url"
          value={text}
          onChange={(e) => onChange({ answer_text: e.target.value })}
          placeholder={question.placeholder ?? 'https://'}
          className="font-serif text-lg"
        />
      )}

      {question.type === 'date' && (
        <DatePickerField
          id={`q-${question.id}`}
          mode="day"
          value={text}
          onChange={(next) => onChange({ answer_text: next })}
        />
      )}

      {question.type === 'month' && (
        <DatePickerField
          id={`q-${question.id}`}
          mode="month"
          value={text}
          onChange={(next) => onChange({ answer_text: next })}
        />
      )}

      {question.type === 'number' && (
        <Input
          id={`q-${question.id}`}
          type="number"
          inputMode="decimal"
          min={question.min}
          max={question.max}
          value={num === null ? '' : String(num)}
          onChange={(e) => {
            const raw = e.target.value
            onChange({
              answer_number: raw === '' ? null : Number(raw),
            })
          }}
          className="font-serif text-lg max-w-xs"
        />
      )}

      {question.type === 'radio' && options.length > 0 && (
        <RadioGroup
          value={choice}
          onValueChange={(v) => onChange({ answer_choice: v })}
          className="space-y-2"
        >
          {options.map((c) => {
            const isSelected = choice === c
            return (
              <Label
                key={c}
                htmlFor={`q-${question.id}-${c}`}
                className={`flex items-center gap-3 rounded-md border px-4 py-3 cursor-pointer font-serif text-base font-normal transition-colors ${
                  isSelected
                    ? 'border-vermillion bg-white'
                    : 'border-black/10 bg-white/40 hover:bg-white/70'
                }`}
              >
                <RadioGroupItem
                  value={c}
                  id={`q-${question.id}-${c}`}
                  className="shrink-0"
                />
                <span className="leading-snug">{c}</span>
              </Label>
            )
          })}
        </RadioGroup>
      )}

      {question.type === 'select' && options.length > 0 && (
        <Select
          value={choice}
          onValueChange={(v) => onChange({ answer_choice: v })}
        >
          <SelectTrigger id={`q-${question.id}`} className="font-serif text-lg max-w-sm">
            <SelectValue placeholder={question.placeholder ?? 'Pick one…'} />
          </SelectTrigger>
          <SelectContent>
            {options.map((c) => (
              <SelectItem key={c} value={c} className="font-serif">
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {question.type === 'checkbox_group' && options.length > 0 && (
        <div className="space-y-2">
          {options.map((c) => {
            const isChecked = checkedSet.has(c)
            return (
              <Label
                key={c}
                htmlFor={`q-${question.id}-${c}`}
                className={`flex items-center gap-3 rounded-md border px-4 py-3 cursor-pointer font-serif text-base font-normal transition-colors ${
                  isChecked
                    ? 'border-vermillion bg-white'
                    : 'border-black/10 bg-white/40 hover:bg-white/70'
                }`}
              >
                <Checkbox
                  id={`q-${question.id}-${c}`}
                  checked={isChecked}
                  onCheckedChange={(next) => {
                    const updated = new Set(checkedSet)
                    if (next) updated.add(c)
                    else updated.delete(c)
                    onChange({
                      answer_choice: JSON.stringify(Array.from(updated)),
                    })
                  }}
                  className="shrink-0"
                />
                <span className="leading-snug">{c}</span>
              </Label>
            )
          })}
        </div>
      )}
    </div>
  )
}
